import { narrativePlainText } from "../../utils/narrativeHtml";
export const CONTENT_STATS = [
  {
    id: "job_postings",
    label: "Job Postings",
    description: "Total live job postings. Draft items are excluded.",
  },
  {
    id: "active_sequences",
    label: "Active Sequences",
    description: "Sequences currently running.",
  },
  {
    id: "templates",
    label: "Templates",
    description: "Total templates.",
  },
  {
    id: "pitch_packages",
    label: "Pitch Packages",
    description: "Total pitch packages.",
  },
  {
    id: "proposals",
    label: "Proposals",
    description: "Total proposals.",
  },
  {
    id: "fee_agreements",
    label: "Fee Agreements",
    description: "Total agreements.",
  },
  {
    id: "published_content",
    label: "Published Content",
    description: "Content currently available for use.",
  },
  {
    id: "draft_content",
    label: "Draft Content",
    description: "Content not yet available.",
  },
  {
    id: "ai_generated_content",
    label: "AI Generated Content",
    description: "Content generated by AI Agents.",
  },
  {
    id: "content_usage",
    label: "Content Usage",
    description: "Total executions of content.",
  },
];

export const CONTENT_TABS = [
  { id: "job_postings", label: "Job Postings", badge: 0 },
  { id: "sequences", label: "Sequences", badge: 0 },
  { id: "templates", label: "Templates", badge: 0 },
  { id: "pitch_packages", label: "Pitch Packages", badge: 0 },
  { id: "proposals", label: "Proposals", badge: 0 },
  { id: "fee_agreements", label: "Fee Agreements", badge: 0 },
];

export const CONTENT_USAGE_STAT_OPTIONS = [
  {
    id: "all_usage",
    label: "All Usage",
    description:
      "Total executions across sequence launches, template sends, proposal sends, and pitch package shares.",
  },
  {
    id: "sequence_launches",
    label: "Sequence Launches",
    description: "Total sequence launches and enrollments.",
    tabId: "sequences",
  },
  {
    id: "template_sends",
    label: "Template Sends",
    description: "Total template sends.",
    tabId: "templates",
  },
  {
    id: "proposal_sends",
    label: "Proposal Sends",
    description: "Total proposal sends.",
    tabId: "proposals",
  },
  {
    id: "pitch_package_shares",
    label: "Pitch Package Shares",
    description: "Total pitch package shares.",
    tabId: "pitch_packages",
  },
];

export const DEFAULT_CONTENT_USAGE_STAT_OPTION_ID = CONTENT_USAGE_STAT_OPTIONS[0].id;

export const STATUS_STYLES = {
  New: { backgroundColor: "#EEF4FF", color: "#3451F4" },
  Draft: { backgroundColor: "#EEF2FF", color: "#4F46E5" },
  Active: { backgroundColor: "#ECFDF3", color: "#16A34A" },
  Paused: { backgroundColor: "#FFF7ED", color: "#EA580C" },
  Offered: { backgroundColor: "#FFF4E5", color: "#F79009" },
  "On hold": { backgroundColor: "#ECFDF3", color: "#12B76A" },
  "For Review": { backgroundColor: "#FFF0E8", color: "#F97316" },
  Closed: { backgroundColor: "#FEF3F2", color: "#F04438" },
  Pending: { backgroundColor: "#FFF6E0", color: "#F59E0B" },
  Open: { backgroundColor: "#ECFDF3", color: "#12B76A" },
  "In Progress": { backgroundColor: "#ECF3FF", color: "#3B82F6" },
  Archived: { backgroundColor: "#EEF2F6", color: "#667085" },
  Approved: { backgroundColor: "#FFF8DB", color: "#CA8A04" },
  Rejected: { backgroundColor: "#FEECEE", color: "#E11D48" },
  Completed: { backgroundColor: "#FFF4E8", color: "#F97316" },
  Scheduled: { backgroundColor: "#FDF2FA", color: "#C11574" },
};

export const STATUS_OPTIONS = Object.keys(STATUS_STYLES);
export const SEQUENCE_STATUS_OPTIONS = ["Draft", "Active", "Paused", "Completed"];
const CONTENT_TYPE_KEYS_BY_TAB = {
  job_postings: "job_posting",
  sequences: "sequence",
  templates: "template",
  pitch_packages: "pitch_package",
  proposals: "proposal",
  fee_agreements: "fee_agreement",
};
const CONTENT_TYPE_LABELS_BY_TAB = {
  job_postings: "Job Posting",
  sequences: "Sequence",
  templates: "Template",
  pitch_packages: "Pitch Package",
  proposals: "Proposal",
  fee_agreements: "Fee Agreement",
};
const RELATED_ENTITY_LABELS_BY_TAB = {
  job_postings: "Job",
  templates: "Candidate",
  pitch_packages: "Company",
  proposals: "Client",
  fee_agreements: "Client",
};
const USAGE_CONTEXT_BY_TAB = {
  job_postings: "Applicants",
  sequences: "Candidates Added",
  templates: "Times Sent",
  pitch_packages: "Pitches Shared",
  proposals: "Proposals Sent",
  fee_agreements: "Agreements Sent",
};
const PERFORMANCE_LABEL_BY_TAB = {
  job_postings: "Interview Rate",
  sequences: "Reply Rate",
  templates: "Open Rate",
  pitch_packages: "Reply Rate",
  proposals: "Acceptance Rate",
  fee_agreements: "Acceptance Rate",
};
const LIVE_CONTENT_EXCLUDED_STATUSES = new Set([
  "draft",
  "archived",
  "closed",
  "completed",
  "rejected",
]);

export const FILTER_PRESETS = [
  { id: "all", label: "All content" },
  { id: "job_board", label: "Job Board only" },
  { id: "linkedin", label: "LinkedIn only" },
  { id: "high_match", label: "Best matches 80%+" },
  { id: "active", label: "Open work only" },
];

export const SEQUENCE_FILTER_PRESETS = [
  { id: "all", label: "All saved sequences" },
  { id: "active", label: "Active only" },
  { id: "draft", label: "Saved drafts" },
  { id: "paused", label: "Paused only" },
  { id: "completed", label: "Completed only" },
];

export const TEMPLATE_FILTER_PRESETS = [
  { id: "all", label: "All templates" },
  { id: "shared", label: "Shared templates" },
  { id: "top_opened", label: "Top opened" },
  { id: "recent", label: "Recently used" },
];

export const CONTENT_COLUMNS = [
  { id: "content_name", label: "Content Name" },
  { id: "content_type_label", label: "Content Type" },
  { id: "status", label: "Status" },
  { id: "related_entity_label", label: "Related Entity" },
  { id: "usage_sort", label: "Usage" },
  { id: "owner_name", label: "Owner" },
  { id: "last_used_sort", label: "Last Used" },
  { id: "performance_sort", label: "Performance" },
  { id: "actions", label: "" },
];

function getContentTypeKey(tabId) {
  return CONTENT_TYPE_KEYS_BY_TAB[tabId] || "content";
}

function getContentTypeLabel(tabId) {
  return CONTENT_TYPE_LABELS_BY_TAB[tabId] || "Content";
}

function getRelatedEntityLabel(tabId, value = "") {
  const normalized = String(value || "").trim();
  return normalized || RELATED_ENTITY_LABELS_BY_TAB[tabId] || "Lead";
}

function getUsageContext(tabId, value = "") {
  const normalized = String(value || "").trim();
  return normalized || USAGE_CONTEXT_BY_TAB[tabId] || "Used";
}

function getPerformanceLabel(tabId, value = "") {
  const normalized = String(value || "").trim();
  return normalized || PERFORMANCE_LABEL_BY_TAB[tabId] || "Performance";
}

function toFiniteNumber(value) {
  if (value == null || value === "") return null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

export const LIBRARY_TABS = ["templates", "pitch_packages", "proposals", "fee_agreements"];

export function createContentRowFromApi(record = {}, index = 0, tabOverride) {
  const tabId = tabOverride || CONTENT_TABS.find(
    (tab) => getContentTypeKey(tab.id) === record.content_type
  )?.id;
  if (!tabId) throw new Error("Unknown content type returned by the server.");
  const type = getContentTypeKey(tabId);
  const usage = toFiniteNumber(record.usage_value);
  const performance = toFiniteNumber(record.performance_value);
  const timestamp = Date.parse(record.last_used_at || "");
  const owner = record.owner_name || record.owner || record.assigned_team_member || "Unassigned";
  const title = record.title || record.job_title || "Untitled";
  const description = record.description || "";
  const usageContext = getUsageContext(tabId);
  let tags = record.tags || [];
  if (typeof tags === "string") {
    try { tags = JSON.parse(tags); } catch { tags = tags.split(",").map((tag) => tag.trim()).filter(Boolean); }
  }
  return {
    ...record,
    id: record.id,
    tabId,
    sortOrder: index + 1,
    contentType: type,
    content_type_key: type,
    content_type_label: getContentTypeLabel(tabId),
    content_name: title,
    job_title: title,
    content_description: narrativePlainText(description),
    description,
    template_content: type === "template" ? description : undefined,
    status: record.status || "Draft",
    owner_name: owner,
    owner: owner,
    owner_initials: getInitials(owner),
    tags: Array.isArray(tags) ? tags : [],
    related_entity_label: getRelatedEntityLabel(tabId, record.related_entity_label),
    usage_value: usage,
    usage_context: usageContext,
    usage_label: usage == null ? "—" : `${usage} ${usageContext}`,
    usage_sort: usage == null ? 0 : -usage,
    performance_value: performance,
    performance_label: getPerformanceLabel(tabId),
    performance_display: performance == null ? "—" : `${performance}%`,
    performance_sort: performance == null ? 0 : -performance,
    last_used_at: Number.isFinite(timestamp) ? record.last_used_at : null,
    last_used_label: Number.isFinite(timestamp) ? formatDateLabel(timestamp) : "—",
    last_used_sort: Number.isFinite(timestamp) ? -timestamp : 0,
    last_used_days_ago: Number.isFinite(timestamp) ? Math.max(0, Math.floor((Date.now() - timestamp) / 86400000)) : null,
    is_ai_generated: record.is_ai_generated === true || Number(record.is_ai_generated) === 1,
  };
}

export function createJobRowFromApi(job = {}, index = 0) {
  return {
    ...createContentRowFromApi({
      ...job,
      title: job.job_title || job.title,
      usage_value: job.application_count ?? job.applications ?? job.num_applications,
      performance_value: job.interview_rate,
    }, index, "job_postings"),
    jobId: job.id,
    company_name: job.linked_company_name || job.company_name || "",
    company_domain: job.linked_company_website || job.company_website || "",
    source: job.source || "",
  };
}

function getNormalizedRowStatus(row = {}) {
  return String(row.statusRaw || row.status || "").trim().toLowerCase();
}

function getUsageMetricValue(row = {}) {
  return Math.max(
    0,
    toFiniteNumber(
      row.usage_value ?? row.applications ?? row.application_count ?? row.enrollment_count
    ) ?? 0
  );
}

function getFlatContentRows(rowsByTab = {}) {
  return Object.values(rowsByTab).flatMap((rows) => (Array.isArray(rows) ? rows : []));
}

function isAiGeneratedRow(row = {}) {
  if (row.is_ai_generated || row.ai_generated || row.generated_by_ai) return true;

  const sourceValues = [
    row.content_source_label,
    row.content_source,
    row.content_origin,
    row.creation_source,
    row.origin,
    row.generated_by,
    row.generated_via,
    row.source,
  ];

  return sourceValues.some((value) => String(value || "").trim().toLowerCase().includes("ai"));
}

function getContentUsageMetricOption(metricId = DEFAULT_CONTENT_USAGE_STAT_OPTION_ID) {
  return (
    CONTENT_USAGE_STAT_OPTIONS.find((option) => option.id === metricId) ||
    CONTENT_USAGE_STAT_OPTIONS[0]
  );
}

function matchesContentUsageMetric(row = {}, metricId = DEFAULT_CONTENT_USAGE_STAT_OPTION_ID) {
  const tabId = String(row.tabId || "").trim();

  switch (metricId) {
    case "sequence_launches":
      return tabId === "sequences";
    case "template_sends":
      return tabId === "templates";
    case "proposal_sends":
      return tabId === "proposals";
    case "pitch_package_shares":
      return tabId === "pitch_packages";
    case "all_usage":
    default:
      return ["sequences", "templates", "proposals", "pitch_packages"].includes(tabId);
  }
}

export function getPrimaryContentStatIdForTab(tabId = "") {
  switch (String(tabId || "").trim()) {
    case "job_postings":
      return null;
    case "templates":
      return "templates";
    case "pitch_packages":
      return "pitch_packages";
    case "proposals":
      return "proposals";
    case "fee_agreements":
      return "fee_agreements";
    default:
      return null;
  }
}

export function getContentStatTargetTabId(
  statId = "",
  usageMetricId = DEFAULT_CONTENT_USAGE_STAT_OPTION_ID
) {
  switch (String(statId || "").trim()) {
    case "job_postings":
      return "job_postings";
    case "active_sequences":
      return "sequences";
    case "templates":
      return "templates";
    case "pitch_packages":
      return "pitch_packages";
    case "proposals":
      return "proposals";
    case "fee_agreements":
      return "fee_agreements";
    case "content_usage":
      return getContentUsageMetricOption(usageMetricId).tabId || null;
    default:
      return null;
  }
}

export function matchesContentStatFilter(
  row = {},
  statId = "",
  { usageMetricId = DEFAULT_CONTENT_USAGE_STAT_OPTION_ID } = {}
) {
  const normalizedStatId = String(statId || "").trim();
  const normalizedStatus = getNormalizedRowStatus(row);
  const rowTabId = String(row.tabId || "").trim();

  switch (normalizedStatId) {
    case "job_postings":
      return rowTabId === "job_postings" && !LIVE_CONTENT_EXCLUDED_STATUSES.has(normalizedStatus);
    case "active_sequences":
      return rowTabId === "sequences" && normalizedStatus === "active";
    case "templates":
      return rowTabId === "templates";
    case "pitch_packages":
      return rowTabId === "pitch_packages";
    case "proposals":
      return rowTabId === "proposals";
    case "fee_agreements":
      return rowTabId === "fee_agreements";
    case "published_content":
      return !LIVE_CONTENT_EXCLUDED_STATUSES.has(normalizedStatus);
    case "draft_content":
      return normalizedStatus === "draft";
    case "ai_generated_content":
      return isAiGeneratedRow(row);
    case "content_usage":
      return matchesContentUsageMetric(row, usageMetricId) && getUsageMetricValue(row) > 0;
    default:
      return true;
  }
}

function getInitials(value, fallback = "NA") {
  const parts = String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return fallback;
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");
}

function formatDateLabel(value) {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function createInitialRowsByTab() {
  return CONTENT_TABS.reduce((acc, tab) => {
    acc[tab.id] = [];
    return acc;
  }, {});
}

export function createTopStats(
  rowsByTab = {},
  { contentUsageMetricId = DEFAULT_CONTENT_USAGE_STAT_OPTION_ID } = {}
) {
  const allRows = getFlatContentRows(rowsByTab);
  const usageMetric = getContentUsageMetricOption(contentUsageMetricId);

  return CONTENT_STATS.map((stat) => {
    if (stat.id === "content_usage") {
      const totalUsage = allRows.reduce((sum, row) => {
        if (!matchesContentUsageMetric(row, contentUsageMetricId)) return sum;
        return sum + getUsageMetricValue(row);
      }, 0);

      return {
        ...stat,
        value: totalUsage,
        selectedOptionId: usageMetric.id,
        selectedOptionLabel: usageMetric.label,
        description: `${stat.description} ${usageMetric.description}`.trim(),
      };
    }

    return {
      ...stat,
      value: allRows.filter((row) =>
        matchesContentStatFilter(row, stat.id, { usageMetricId: contentUsageMetricId })
      ).length,
    };
  });
}

